import type { CompanyRegistryBeneficiary, KYC } from '../../webapp/src/types';

export const BENEFICIAL_OWNER_SCREENING_THRESHOLD = 25;

/** Percentages are percentage points, never fractions or rounded integers. */
export function beneficialOwnerPercentage(value: unknown): number | undefined {
    if (typeof value !== 'string' && typeof value !== 'number') return undefined;
    const text = String(value).trim();
    if (!/^\d+(?:\.\d+)?\s*%?$/.test(text)) return undefined;
    const percentage = Number(text.replace(/\s*%$/, ''));
    return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100 ? percentage : undefined;
}

export function isScreenableBeneficialOwner(owner: CompanyRegistryBeneficiary): boolean {
    const percentage = beneficialOwnerPercentage(owner.ownership_percentage);
    return percentage !== undefined && percentage >= BENEFICIAL_OWNER_SCREENING_THRESHOLD;
}

/** Unknown ownership must be corrected rather than silently bypassing screening. */
export function validateBeneficialOwnerPercentages(kyc: Pick<KYC, 'beneficiaries'>): void {
    if (kyc.beneficiaries === undefined) return;
    if (!Array.isArray(kyc.beneficiaries)) throw new Error('Beneficial owners must be a list');
    for (const [index, owner] of kyc.beneficiaries.entries()) {
        if (!owner || beneficialOwnerPercentage(owner.ownership_percentage) === undefined) {
            throw new Error(`Beneficial owner ${index + 1}: a valid ownership percentage from 0 to 100 is required`);
        }
    }
}

export function getBeneficialOwnerIds(kyc: Pick<KYC, 'beneficiaries'>): string[] {
    return [...new Set((kyc.beneficiaries ?? []).filter(isScreenableBeneficialOwner)
        .map(owner => owner.customerID).filter((id): id is string => !!id))].sort();
}
